import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import './DAGProgressIndicator.css';

/**
 * DAG Progress Indicator - Shows real-time progress of reasoning agent DAG nodes
 * Displays a todo-list style progress tracker showing what the AI is currently doing
 * After completion, collapses into an accordion that can be expanded to review the execution plan
 */
const DAGProgressIndicator = ({
  dagProgress = [],
  currentDagNode = null,
  isVisible = true,
  isComplete = false, // New: indicates if reasoning is complete
}) => {
  const [isExpanded, setIsExpanded] = useState(true); // Start expanded while running

  // Create a unified progress list combining history and current node
  // This must be done before any hooks that depend on it
  const progressItems = useMemo(() => {
    const items = [...dagProgress];
    
    // Add current node if it's not already in the progress list
    if (currentDagNode && !items.find(item => 
      item.nodeName === currentDagNode.nodeName && 
      item.timestamp === currentDagNode.timestamp
    )) {
      items.push(currentDagNode);
    }

    // Sort by timestamp
    items.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    return items;
  }, [dagProgress, currentDagNode]);

  // Calculate summary stats for the collapsed view
  const stats = useMemo(() => {
    const completed = progressItems.filter(item => item.status === 'completed').length;
    const errors = progressItems.filter(item => item.status === 'error').length;
    const total = progressItems.length;
    const dagNodes = progressItems.filter(item => item.eventType === 'dag_node' || item.eventType === 'node_execution');
    return { completed, errors, total, dagNodes: dagNodes.length };
  }, [progressItems]);

  // Keep expanded - user can manually collapse if they want

  // Early return AFTER all hooks
  if (!isVisible || (progressItems.length === 0)) {
    return null;
  }

  const getStatusIcon = (status, isCurrent) => {
    if (isCurrent && status === 'running') {
      return <PlayArrowIcon className="status-icon running pulsing" fontSize="small" />;
    }
    
    switch (status) {
      case 'completed':
      case 'success':
        return <CheckCircleIcon className="status-icon completed" fontSize="small" />;
      case 'running':
      case 'in_progress':
        return <PlayArrowIcon className="status-icon running" fontSize="small" />;
      case 'pending':
        return <HourglassEmptyIcon className="status-icon pending" fontSize="small" />;
      case 'error':
      case 'failed':
        return <ErrorIcon className="status-icon error" fontSize="small" />;
      default:
        return <HourglassEmptyIcon className="status-icon pending" fontSize="small" />;
    }
  };

  const getStatusClass = (status, isCurrent) => {
    if (isCurrent) return 'current';
    return status || 'pending';
  };

  const formatNodeName = (nodeName, eventType, nodeType) => {
    if (!nodeName) return 'Processing...';
    
    // Get icon based on event type and node type
    const getIcon = () => {
      if (eventType === 'planner') return '📋';
      if (eventType === 'director') return '🎬';
      if (eventType === 'manager') return '📊';
      if (eventType === 'reasoning') return '🧠';
      
      // For dag_node and node_execution, use node type
      if (nodeType === 'CHAT_COMPLETION') return '💬';
      if (nodeType === 'WEB_SEARCH') return '🔍';
      if (nodeType === 'RAG') return '📚';
      if (nodeType === 'CODE_EXECUTION') return '💻';
      
      if (eventType === 'dag_node') return '⏳';
      if (eventType === 'node_execution') return '▶️';
      
      return '⚙️';
    };
    
    // Format the display name
    const formatName = (name) => {
      return name
        .replace(/_(\d+)$/, ' $1') // Move trailing numbers
        .replace(/[_-]/g, ' ') // Replace underscores and hyphens
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
        .trim();
    };
    
    // Special names for known phases
    const phaseNames = {
      'planner': 'Planning',
      'director': 'Creating Execution Plan',
      'manager': 'Orchestrating',
      'reasoning': 'Finalizing',
    };
    
    if (phaseNames[eventType]) {
      return `${getIcon()} ${phaseNames[eventType]}`;
    }
    
    // For DAG nodes, format the node name nicely
    return `${getIcon()} ${formatName(nodeName)}`;
  };

  const formatDescription = (item) => {
    const { description, instruction, nodeName, eventType, stepNumber, totalSteps } = item;
    
    // For DAG nodes and executions, use the actual description from the DAG
    if ((eventType === 'dag_node' || eventType === 'node_execution') && description) {
      // If we have a step number, show it
      const stepPrefix = stepNumber && totalSteps 
        ? `Step ${stepNumber}/${totalSteps}: ` 
        : '';
      
      // Use the description directly - it comes from the DAG graph
      let desc = description;
      
      // Truncate if too long
      if (desc.length > 150) {
        desc = desc.substring(0, 147) + '...';
      }
      
      return stepPrefix + desc;
    }
    
    // For other event types, use the description if provided
    if (description && description.length > 5) {
      if (description.length > 200) {
        return description.substring(0, 197) + '...';
      }
      return description;
    }
    
    // Fallback descriptions
    const eventDescMap = {
      'planner': 'Planning the approach and breaking down the task...',
      'director': 'Designing the execution graph...',
      'manager': 'Coordinating the execution sequence...',
      'reasoning': 'Generating the final response...',
    };
    
    if (eventType && eventDescMap[eventType]) {
      return eventDescMap[eventType];
    }
    
    return 'Processing...';
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`dag-progress-indicator ${isComplete ? 'complete' : 'running'} ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <div className="progress-header" onClick={toggleExpanded} role="button" tabIndex={0}>
        <div className="progress-title">
          <span className="brain-icon">{isComplete ? '✅' : '🧠'}</span>
          <span>{isComplete ? 'Execution Plan' : 'AI Reasoning Progress'}</span>
        </div>
        <div className="progress-header-right">
          {isComplete && (
            <span className="progress-summary">
              {stats.errors > 0 
                ? `${stats.completed}/${stats.total} steps (${stats.errors} errors)` 
                : `${stats.dagNodes} steps completed`}
            </span>
          )}
          <button className="expand-toggle" aria-label={isExpanded ? 'Collapse' : 'Expand'}>
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </button>
        </div>
      </div>
      
      {isExpanded && (
        <div className="progress-list">
          {progressItems.map((item, index) => {
            const isCurrent = currentDagNode && 
              item.nodeName === currentDagNode.nodeName && 
              item.timestamp === currentDagNode.timestamp;
            
            return (
              <div 
                key={`${item.nodeName}-${item.timestamp}-${index}`}
                className={`progress-item ${getStatusClass(item.status, isCurrent)}`}
              >
                <div className="progress-item-icon">
                  {getStatusIcon(item.status, isCurrent)}
                </div>
                
                <div className="progress-item-content">
                  <div className="progress-item-title">
                    {formatNodeName(item.nodeName, item.eventType, item.nodeType || item.details?.type)}
                  </div>
                  <div className="progress-item-description">
                    {formatDescription(item)}
                  </div>
                  {item.instruction && item.eventType === 'dag_node' && (
                    <div className="progress-item-instruction">
                      {item.instruction.length > 100 ? item.instruction.substring(0, 97) + '...' : item.instruction}
                    </div>
                  )}
                  {item.status === 'error' && item.details?.error && (
                    <div className="progress-item-error">
                      {item.details.error.substring(0, 100)}
                    </div>
                  )}
                  {isCurrent && (
                    <div className="progress-pulse"></div>
                  )}
                </div>
                
                {isCurrent && (
                  <div className="current-indicator">
                    <div className="pulse-dot"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

DAGProgressIndicator.propTypes = {
  /** Array of DAG progress updates */
  dagProgress: PropTypes.arrayOf(PropTypes.shape({
    nodeName: PropTypes.string,
    description: PropTypes.string,
    instruction: PropTypes.string,
    status: PropTypes.string,
    timestamp: PropTypes.string,
    eventType: PropTypes.string,
    nodeType: PropTypes.string,
    stepNumber: PropTypes.number,
    totalSteps: PropTypes.number,
    details: PropTypes.object,
  })),
  
  /** Current DAG node being processed */
  currentDagNode: PropTypes.shape({
    nodeName: PropTypes.string,
    description: PropTypes.string,
    instruction: PropTypes.string,
    status: PropTypes.string,
    timestamp: PropTypes.string,
    eventType: PropTypes.string,
    nodeType: PropTypes.string,
  }),
  
  /** Whether the indicator should be visible */
  isVisible: PropTypes.bool,
  
  /** Whether the reasoning process is complete (enables accordion collapse) */
  isComplete: PropTypes.bool,
};

export default DAGProgressIndicator;
